import { CinematicModal } from "@/components/ui/CinematicModal";
import {
  cinematicModalSecondaryButtonClass,
  cinematicModalSubtitleClass,
  cinematicModalTitleClass,
} from "@/lib/cinematicModalStyles";
import { UserLevel } from "@/lib/levelingSystem";
import { AnimatePresence, motion } from "framer-motion";
import { Award, Crown, Sparkles, Star, Trophy, Zap } from "lucide-react";
import React, { useState } from "react";

interface UserLevelDisplayProps {
  userEmail: string | null;
  userLevel: UserLevel | null;
  loading: boolean;
  onLevelUpdate?: (level: UserLevel) => void;
  showHoverDetails?: boolean; // Disable hover tooltip (e.g., inside hamburger)
  usePortalForModal?: boolean; // Render modal into document.body to avoid ancestor transforms
}

const UserLevelDisplay: React.FC<UserLevelDisplayProps> = ({
  userEmail,
  userLevel,
  loading,
  onLevelUpdate,
  showHoverDetails = true,
  usePortalForModal = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  React.useEffect(() => {
    if (userLevel && onLevelUpdate) {
      onLevelUpdate(userLevel);
    }
  }, [userLevel, onLevelUpdate]);

  // Don't show anything if there's no user email or if it's a tester
  if (!userEmail || userEmail === "tester" || loading) {
    return null;
  }

  if (!userLevel) {
    return null; // Silently fail - level display is not critical
  }

  // Get appropriate icon based on level
  const getLevelIcon = (level: number) => {
    if (level >= 9) return Trophy;
    if (level >= 7) return Crown;
    if (level >= 4) return Award;
    if (level >= 2) return Star;
    if (level >= 1) return Zap;
    return Sparkles;
  };

  const IconComponent = getLevelIcon(userLevel.level);

  return (
    <div className="relative">
      {/* Main Level Badge */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 20,
          delay: 0.2,
        }}
        className="relative"
      >
        <motion.button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          onMouseEnter={showHoverDetails ? () => setShowDetails(true) : undefined}
          onMouseLeave={showHoverDetails ? () => setShowDetails(false) : undefined}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="relative flex h-10 w-full items-center gap-2 overflow-hidden rounded-md border border-white/10 bg-white/[0.025] px-3 text-left text-sm font-semibold text-white/80 transition-colors hover:border-white/20 hover:bg-white/[0.06] hover:text-white"
          aria-label={`${userLevel.title}, level ${userLevel.level}. Open level details.`}
        >
          <IconComponent className="h-4 w-4 shrink-0 text-orange-300" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">{userLevel.title}</span>
          {userLevel.level > 0 && (
            <span className="text-xs text-white/45">Level {userLevel.level}</span>
          )}

          {userLevel.level < 50 && userLevel.progress < 100 && (
            <div className="absolute inset-x-0 bottom-0 h-px bg-white/[0.08]">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${userLevel.progress}%` }}
                transition={{ delay: 0.5, duration: 1.5, ease: "easeOut" }}
                className="h-full bg-orange-300/80"
              />
            </div>
          )}
        </motion.button>

        {/* Tooltip on hover */}
        <AnimatePresence>
          {showHoverDetails && showDetails && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="absolute left-1/2 top-full z-50 mt-2 w-64 max-w-[min(16rem,calc(100vw-1.5rem))] -translate-x-1/2 rounded-lg border border-white/10 bg-[#121318] px-4 py-3 shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
            >
              <div className="mb-1 text-sm font-semibold text-white">
                {userLevel.title} - {userLevel.subtitle}
              </div>
              <div className="mb-2 text-xs text-white/65">{userLevel.description}</div>
              {userLevel.level < 50 && userLevel.progress < 100 && (
                <div className="text-xs text-white/50">
                  Progress to next level: {userLevel.progress}%
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <CinematicModal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        titleId="user-level-dialog-title"
        usePortal={usePortalForModal}
      >
        <div className="flex items-start justify-between gap-4 pr-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-orange-400/25 bg-orange-500/10">
              <IconComponent className="h-5 w-5 text-orange-300" aria-hidden="true" />
            </div>
            <div>
              <h3 id="user-level-dialog-title" className={cinematicModalTitleClass}>
                {userLevel.title}
              </h3>
              <p className={cinematicModalSubtitleClass}>{userLevel.subtitle}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-white">{userLevel.level}</div>
            <div className="text-xs font-semibold uppercase tracking-wide text-white/45">Level</div>
          </div>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-white/75">{userLevel.description}</p>

        {userLevel.level < 50 && userLevel.progress < 100 && (
          <div className="mt-4 border-y border-white/10 py-4">
            <div className="mb-2 flex justify-between text-sm">
              <span className="text-white/60">Progress to next level</span>
              <span className="font-semibold text-white">{userLevel.progress}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/10">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${userLevel.progress}%` }}
                transition={{ duration: 1.2, ease: "easeOut" }}
                className="h-full rounded-full bg-orange-300"
              />
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setIsExpanded(false)}
          className={`${cinematicModalSecondaryButtonClass} mt-6 w-full`}
        >
          Continue learning
        </button>
      </CinematicModal>
    </div>
  );
};

export default UserLevelDisplay;
